import { useState, useMemo } from 'react'
import {
  TrendingUp,
  Eye,
  MousePointer,
  DollarSign,
  Zap,
  BarChart3,
  Filter,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react'

// Mock data for ad platforms
const mockAdPlatforms = [
  {
    id: 'GOOGLE-001',
    platform: 'google_ads',
    name: 'Google Ads',
    icon: '🔍',
    color: 'bg-blue-100 text-blue-800',
    campaigns: 8,
    status: 'active',
    metrics: {
      impressions: 245680,
      clicks: 8932,
      spend: 2847.50,
      conversions: 156,
      ctr: 3.64,
      cpc: 0.32,
      cpa: 18.25,
      roas: 3.2,
    },
    trend: 'up',
    lastUpdated: new Date(Date.now() - 2 * 60000),
  },
  {
    id: 'META-001',
    platform: 'meta_ads',
    name: 'Meta (Facebook + Instagram)',
    icon: 'f',
    color: 'bg-blue-100 text-blue-900',
    campaigns: 12,
    status: 'active',
    metrics: {
      impressions: 356200,
      clicks: 12450,
      spend: 3582.75,
      conversions: 428,
      ctr: 3.50,
      cpc: 0.29,
      cpa: 8.37,
      roas: 4.8,
    },
    trend: 'up',
    lastUpdated: new Date(Date.now() - 3 * 60000),
  },
  {
    id: 'TIKTOK-001',
    platform: 'tiktok_ads',
    name: 'TikTok Ads',
    icon: '🎵',
    color: 'bg-black text-white',
    campaigns: 5,
    status: 'active',
    metrics: {
      impressions: 482100,
      clicks: 18940,
      spend: 2150.00,
      conversions: 234,
      ctr: 3.93,
      cpc: 0.11,
      cpa: 9.19,
      roas: 5.2,
    },
    trend: 'up',
    lastUpdated: new Date(Date.now() - 4 * 60000),
  },
  {
    id: 'YOUTUBE-001',
    platform: 'youtube_ads',
    name: 'YouTube Ads',
    icon: '▶️',
    color: 'bg-red-100 text-red-800',
    campaigns: 6,
    status: 'active',
    metrics: {
      impressions: 156400,
      clicks: 3120,
      spend: 1845.25,
      conversions: 89,
      ctr: 1.99,
      cpc: 0.59,
      cpa: 20.73,
      roas: 2.4,
    },
    trend: 'down',
    lastUpdated: new Date(Date.now() - 5 * 60000),
  },
  {
    id: 'LINKEDIN-001',
    platform: 'linkedin_ads',
    name: 'LinkedIn Ads',
    icon: '👔',
    color: 'bg-blue-700 text-white',
    campaigns: 3,
    status: 'active',
    metrics: {
      impressions: 45670,
      clicks: 892,
      spend: 1245.50,
      conversions: 67,
      ctr: 1.95,
      cpc: 1.40,
      cpa: 18.59,
      roas: 2.1,
    },
    trend: 'up',
    lastUpdated: new Date(Date.now() - 6 * 60000),
  },
  {
    id: 'PINTEREST-001',
    platform: 'pinterest_ads',
    name: 'Pinterest Ads',
    icon: 'P',
    color: 'bg-red-600 text-white',
    campaigns: 2,
    status: 'active',
    metrics: {
      impressions: 89234,
      clicks: 1456,
      spend: 456.75,
      conversions: 42,
      ctr: 1.63,
      cpc: 0.31,
      cpa: 10.87,
      roas: 3.7,
    },
    trend: 'up',
    lastUpdated: new Date(Date.now() - 7 * 60000),
  },
  {
    id: 'SNAPCHAT-001',
    platform: 'snapchat_ads',
    name: 'Snapchat Ads',
    icon: '👻',
    color: 'bg-yellow-100 text-yellow-800',
    campaigns: 1,
    status: 'paused',
    metrics: {
      impressions: 12300,
      clicks: 245,
      spend: 234.50,
      conversions: 8,
      ctr: 1.99,
      cpc: 0.96,
      cpa: 29.31,
      roas: 1.2,
    },
    trend: 'down',
    lastUpdated: new Date(Date.now() - 8 * 60000),
  },
  {
    id: 'AMAZON-001',
    platform: 'amazon_ads',
    name: 'Amazon Ads',
    icon: '🛒',
    color: 'bg-orange-100 text-orange-800',
    campaigns: 4,
    status: 'active',
    metrics: {
      impressions: 203400,
      clicks: 5678,
      spend: 1932.40,
      conversions: 312,
      ctr: 2.79,
      cpc: 0.34,
      cpa: 6.19,
      roas: 4.5,
    },
    trend: 'up',
    lastUpdated: new Date(Date.now() - 9 * 60000),
  },
]

// Platform info
const platformInfo = {
  google_ads: { label: 'Google Ads', icon: '🔍', color: 'bg-blue-100 text-blue-800' },
  meta_ads: { label: 'Meta Ads', icon: 'f', color: 'bg-blue-100 text-blue-900' },
  tiktok_ads: { label: 'TikTok Ads', icon: '🎵', color: 'bg-black text-white' },
  youtube_ads: { label: 'YouTube Ads', icon: '▶️', color: 'bg-red-100 text-red-800' },
  linkedin_ads: { label: 'LinkedIn Ads', icon: '👔', color: 'bg-blue-700 text-white' },
  pinterest_ads: { label: 'Pinterest Ads', icon: 'P', color: 'bg-red-600 text-white' },
  snapchat_ads: { label: 'Snapchat Ads', icon: '👻', color: 'bg-yellow-100 text-yellow-800' },
  amazon_ads: { label: 'Amazon Ads', icon: '🛒', color: 'bg-orange-100 text-orange-800' },
}

export default function MarketingAIMonitorPage() {
  const [selectedPlatform, setSelectedPlatform] = useState(null)
  const [timeRange, setTimeRange] = useState('today')
  const [autoRefresh, setAutoRefresh] = useState(true)

  // Calculate total metrics
  const totalMetrics = useMemo(() => {
    return mockAdPlatforms.reduce(
      (acc, platform) => ({
        impressions: acc.impressions + platform.metrics.impressions,
        clicks: acc.clicks + platform.metrics.clicks,
        spend: acc.spend + platform.metrics.spend,
        conversions: acc.conversions + platform.metrics.conversions,
      }),
      { impressions: 0, clicks: 0, spend: 0, conversions: 0 }
    )
  }, [])

  const activePlatforms = mockAdPlatforms.filter(p => p.status === 'active')
  const selectedPlatformData = selectedPlatform
    ? mockAdPlatforms.find(p => p.id === selectedPlatform)
    : null

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Marketing AI Monitor</h1>
          <p className="text-blue-200 mt-1">Real-time monitoring across all ad platforms</p>
        </div>
        <button
          onClick={() => setAutoRefresh(!autoRefresh)}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all ${
            autoRefresh
              ? 'bg-green-600/30 text-green-300 border border-green-500/50'
              : 'bg-slate-700/50 text-blue-200 border border-blue-500/30'
          }`}
        >
          <RefreshCw size={18} className={autoRefresh ? 'animate-spin' : ''} />
          <span>{autoRefresh ? 'Auto-Refresh ON' : 'Auto-Refresh OFF'}</span>
        </button>
      </div>

      {/* Alert Banner */}
      {mockAdPlatforms.some(p => p.trend === 'down') && (
        <div className="bg-orange-500/20 border border-orange-500/50 rounded-lg p-4 flex items-start space-x-3">
          <AlertTriangle className="text-orange-400 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-semibold text-orange-300">Performance Alerts</h3>
            <p className="text-sm text-orange-200 mt-1">
              2 campaigns showing declining performance. YouTube Ads and Snapchat Ads need attention.
            </p>
          </div>
        </div>
      )}

      {/* Time Range Selector */}
      <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-4">
        <label className="block text-sm font-medium text-blue-200 mb-3">Date Range</label>
        <div className="flex gap-2">
          {['today', '7days', '30days', 'custom'].map(range => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                timeRange === range
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-700/50 text-blue-200 hover:bg-slate-700/70 border border-blue-500/30'
              }`}
            >
              {range === 'today'
                ? 'Today'
                : range === '7days'
                  ? 'Last 7 Days'
                  : range === '30days'
                    ? 'Last 30 Days'
                    : 'Custom'}
            </button>
          ))}
        </div>
      </div>

      {/* Total Metrics Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg border border-blue-500/30 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-blue-200 font-medium">Total Spend</p>
            <DollarSign className="text-blue-400" size={20} />
          </div>
          <p className="text-3xl font-bold text-white">${totalMetrics.spend.toFixed(2)}</p>
          <p className="text-xs text-blue-300/70 mt-2">Across {activePlatforms.length} platforms</p>
        </div>

        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg border border-blue-500/30 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-blue-200 font-medium">Total Impressions</p>
            <Eye className="text-cyan-400" size={20} />
          </div>
          <p className="text-3xl font-bold text-white">{(totalMetrics.impressions / 1000).toFixed(0)}K</p>
          <p className="text-xs text-blue-300/70 mt-2">Across all ads</p>
        </div>

        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg border border-blue-500/30 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-blue-200 font-medium">Total Clicks</p>
            <MousePointer className="text-green-400" size={20} />
          </div>
          <p className="text-3xl font-bold text-white">{totalMetrics.clicks.toLocaleString()}</p>
          <p className="text-xs text-blue-300/70 mt-2">
            Avg CTR: {((totalMetrics.clicks / totalMetrics.impressions) * 100).toFixed(2)}%
          </p>
        </div>

        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg border border-blue-500/30 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-blue-200 font-medium">Total Conversions</p>
            <TrendingUp className="text-orange-400" size={20} />
          </div>
          <p className="text-3xl font-bold text-white">{totalMetrics.conversions.toLocaleString()}</p>
          <p className="text-xs text-blue-300/70 mt-2">
            Conv Rate: {((totalMetrics.conversions / totalMetrics.clicks) * 100).toFixed(2)}%
          </p>
        </div>
      </div>

      {/* Platform Grid */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-white">Platform Performance</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {mockAdPlatforms.map(platform => (
            <button
              key={platform.id}
              onClick={() => setSelectedPlatform(platform.id)}
              className={`text-left rounded-lg border-2 p-4 transition-all ${
                selectedPlatform === platform.id
                  ? 'border-blue-500 bg-slate-700/50'
                  : 'border-blue-500/30 bg-slate-800/50 hover:border-blue-500/50'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">{platform.icon}</span>
                  <div>
                    <h3 className="font-bold text-white">{platform.name}</h3>
                    <p className="text-xs text-blue-300">
                      {platform.campaigns} campaign{platform.campaigns !== 1 ? 's' : ''} •{' '}
                      <span className={`font-medium ${platform.status === 'active' ? 'text-green-400' : 'text-blue-300'}`}>
                        {platform.status.charAt(0).toUpperCase() + platform.status.slice(1)}
                      </span>
                    </p>
                  </div>
                </div>
                <span
                  className={`text-lg font-bold ${platform.trend === 'up' ? 'text-green-400' : 'text-red-400'}`}
                >
                  {platform.trend === 'up' ? '↑' : '↓'}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-700/50 rounded p-2 border border-blue-500/20">
                  <p className="text-xs text-blue-300">Spend</p>
                  <p className="font-bold text-white">${platform.metrics.spend.toFixed(0)}</p>
                </div>
                <div className="bg-slate-700/50 rounded p-2 border border-blue-500/20">
                  <p className="text-xs text-blue-300">Conversions</p>
                  <p className="font-bold text-white">{platform.metrics.conversions}</p>
                </div>
                <div className="bg-slate-700/50 rounded p-2 border border-blue-500/20">
                  <p className="text-xs text-blue-300">CTR</p>
                  <p className="font-bold text-white">{platform.metrics.ctr.toFixed(2)}%</p>
                </div>
                <div className="bg-slate-700/50 rounded p-2 border border-blue-500/20">
                  <p className="text-xs text-blue-300">ROAS</p>
                  <p className="font-bold text-white">{platform.metrics.roas.toFixed(1)}x</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Detailed Platform View */}
      {selectedPlatformData && (
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg border border-blue-500/30 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <span className="text-3xl">{selectedPlatformData.icon}</span>
              <div>
                <h2 className="text-2xl font-bold text-white">{selectedPlatformData.name} - Detailed Metrics</h2>
                <p className="text-sm text-blue-300">
                  Updated {selectedPlatformData.lastUpdated.toLocaleTimeString()}
                </p>
              </div>
            </div>
            <span
              className={`text-2xl font-bold ${
                selectedPlatformData.trend === 'up' ? 'text-green-400' : 'text-red-400'
              }`}
            >
              {selectedPlatformData.trend === 'up' ? '↑' : '↓'}
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/30">
              <p className="text-sm text-blue-200 mb-1">Impressions</p>
              <p className="text-2xl font-bold text-white">
                {(selectedPlatformData.metrics.impressions / 1000).toFixed(1)}K
              </p>
              <p className="text-xs text-blue-300/70 mt-1">Total views</p>
            </div>

            <div className="bg-green-500/10 rounded-lg p-4 border border-green-500/30">
              <p className="text-sm text-green-200 mb-1">Clicks</p>
              <p className="text-2xl font-bold text-white">{selectedPlatformData.metrics.clicks.toLocaleString()}</p>
              <p className="text-xs text-green-300/70 mt-1">CTR: {selectedPlatformData.metrics.ctr.toFixed(2)}%</p>
            </div>

            <div className="bg-purple-500/10 rounded-lg p-4 border border-purple-500/30">
              <p className="text-sm text-purple-200 mb-1">Spend</p>
              <p className="text-2xl font-bold text-white">${selectedPlatformData.metrics.spend.toFixed(0)}</p>
              <p className="text-xs text-purple-300/70 mt-1">CPC: ${selectedPlatformData.metrics.cpc.toFixed(2)}</p>
            </div>

            <div className="bg-orange-500/10 rounded-lg p-4 border border-orange-500/30">
              <p className="text-sm text-orange-200 mb-1">Conversions</p>
              <p className="text-2xl font-bold text-white">{selectedPlatformData.metrics.conversions}</p>
              <p className="text-xs text-orange-300/70 mt-1">CPA: ${selectedPlatformData.metrics.cpa.toFixed(2)}</p>
            </div>

            <div className="bg-indigo-500/10 rounded-lg p-4 border border-indigo-500/30">
              <p className="text-sm text-indigo-200 mb-1">ROAS</p>
              <p className="text-2xl font-bold text-white">{selectedPlatformData.metrics.roas.toFixed(1)}x</p>
              <p className="text-xs text-indigo-300/70 mt-1">Return on ad spend</p>
            </div>

            <div className="bg-pink-500/10 rounded-lg p-4 border border-pink-500/30">
              <p className="text-sm text-pink-200 mb-1">Campaigns</p>
              <p className="text-2xl font-bold text-white">{selectedPlatformData.campaigns}</p>
              <p className="text-xs text-pink-300/70 mt-1">Active campaigns</p>
            </div>

            <div className="bg-cyan-500/10 rounded-lg p-4 border border-cyan-500/30">
              <p className="text-sm text-cyan-200 mb-1">Avg CPC</p>
              <p className="text-2xl font-bold text-white">${selectedPlatformData.metrics.cpc.toFixed(2)}</p>
              <p className="text-xs text-cyan-300/70 mt-1">Cost per click</p>
            </div>

            <div className="bg-lime-500/10 rounded-lg p-4 border border-lime-500/30">
              <p className="text-sm text-lime-200 mb-1">Status</p>
              <p className={`text-2xl font-bold ${selectedPlatformData.status === 'active' ? 'text-green-400' : 'text-blue-300'}`}>
                {selectedPlatformData.status === 'active' ? '🟢 Active' : '⏸ Paused'}
              </p>
              <p className="text-xs text-blue-300/70 mt-1">Campaign status</p>
            </div>
          </div>

          {/* Recommendations */}
          <div className="mt-6 pt-6 border-t border-blue-500/30">
            <h3 className="font-bold text-white mb-3">AI Recommendations</h3>
            <div className="space-y-2">
              {selectedPlatformData.roas < 3 && (
                <div className="bg-yellow-500/20 border border-yellow-500/50 rounded p-3 flex items-start space-x-2">
                  <Zap className="text-yellow-400 flex-shrink-0 mt-0.5" size={18} />
                  <p className="text-sm text-yellow-200">
                    <strong>Lower ROAS:</strong> Consider optimizing targeting or increasing bid amounts
                  </p>
                </div>
              )}
              {selectedPlatformData.metrics.cpa > 15 && (
                <div className="bg-orange-500/20 border border-orange-500/50 rounded p-3 flex items-start space-x-2">
                  <AlertTriangle className="text-orange-400 flex-shrink-0 mt-0.5" size={18} />
                  <p className="text-sm text-orange-200">
                    <strong>High CPA:</strong> Try A/B testing creative assets or landing pages
                  </p>
                </div>
              )}
              {selectedPlatformData.metrics.ctr < 2 && (
                <div className="bg-blue-500/20 border border-blue-500/50 rounded p-3 flex items-start space-x-2">
                  <Zap className="text-blue-400 flex-shrink-0 mt-0.5" size={18} />
                  <p className="text-sm text-blue-200">
                    <strong>Low CTR:</strong> Improve ad copy or visuals to increase click-through rate
                  </p>
                </div>
              )}
              {selectedPlatformData.trend === 'down' && (
                <div className="bg-red-500/20 border border-red-500/50 rounded p-3 flex items-start space-x-2">
                  <AlertTriangle className="text-red-400 flex-shrink-0 mt-0.5" size={18} />
                  <p className="text-sm text-red-200">
                    <strong>Performance Declining:</strong> Review campaign settings and adjust strategy
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Comparative Analysis */}
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg border border-blue-500/30 p-6">
        <h2 className="text-xl font-bold text-white mb-4">Platform Comparison</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-blue-500/30">
                <th className="text-left py-2 px-3 font-bold text-white">Platform</th>
                <th className="text-right py-2 px-3 font-bold text-white">Spend</th>
                <th className="text-right py-2 px-3 font-bold text-white">CTR</th>
                <th className="text-right py-2 px-3 font-bold text-white">CPA</th>
                <th className="text-right py-2 px-3 font-bold text-white">ROAS</th>
                <th className="text-right py-2 px-3 font-bold text-white">Status</th>
              </tr>
            </thead>
            <tbody>
              {mockAdPlatforms.map(platform => (
                <tr key={platform.id} className="border-b border-blue-500/20 hover:bg-slate-700/50">
                  <td className="py-3 px-3 font-medium text-white">{platform.name}</td>
                  <td className="py-3 px-3 text-right text-blue-200">${platform.metrics.spend.toFixed(0)}</td>
                  <td className="py-3 px-3 text-right">
                    <span className={platform.metrics.ctr > 2.5 ? 'text-green-400 font-bold' : 'text-blue-200'}>
                      {platform.metrics.ctr.toFixed(2)}%
                    </span>
                  </td>
                  <td className="py-3 px-3 text-right">
                    <span className={platform.metrics.cpa < 15 ? 'text-green-400 font-bold' : 'text-blue-200'}>
                      ${platform.metrics.cpa.toFixed(2)}
                    </span>
                  </td>
                  <td className="py-3 px-3 text-right">
                    <span className={platform.metrics.roas > 3 ? 'text-green-400 font-bold' : 'text-blue-200'}>
                      {platform.metrics.roas.toFixed(1)}x
                    </span>
                  </td>
                  <td className="py-3 px-3 text-right">
                    <span
                      className={`px-2 py-1 rounded text-xs font-bold ${
                        platform.status === 'active' ? 'bg-green-500/30 text-green-200' : 'bg-slate-700/50 text-blue-200'
                      }`}
                    >
                      {platform.status.charAt(0).toUpperCase() + platform.status.slice(1)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
